import { apiClient, toServiceError } from '@/services/http/client'
import type { ClientPortalService } from './clientPortal.service'
import type {
  ClientAddress,
  ClientConsent,
  ClientDocument,
  ClientDocumentRequest,
  ClientFiling,
  ClientMessage,
  ClientPayment,
  ClientPortalSnapshot,
  ClientProfile,
  ClientProfileUpdate,
  ClientReminder,
} from '@/types/clientPortal.types'

type Raw = Record<string, unknown>

const object = (value: unknown): Raw =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Raw : {}
const text = (value: unknown): string => value == null ? '' : String(value)
const optionalText = (value: unknown): string | undefined => value == null || value === '' ? undefined : String(value)
const number = (value: unknown): number => Number(value ?? 0) || 0
const items = (value: unknown): Raw[] => {
  if (Array.isArray(value)) return value.map(object)
  const payload = object(value)
  const rows = payload.items ?? payload.notifications ?? payload.results ?? []
  return Array.isArray(rows) ? rows.map(object) : []
}

function address(value: unknown): ClientAddress {
  const row = object(value)
  return {
    line1: optionalText(row.line1 ?? row.address_line1),
    line2: optionalText(row.line2 ?? row.address_line2),
    city: optionalText(row.city),
    state: optionalText(row.state),
    pincode: optionalText(row.pincode ?? row.pin_code),
  }
}

function profile(value: unknown): ClientProfile {
  const row = object(value)
  return {
    customerId: text(row.customer_id ?? row.customerId),
    fullName: text(row.full_name ?? row.fullName),
    pan: text(row.pan),
    aadhaar: optionalText(row.aadhaar),
    dateOfBirth: optionalText(row.date_of_birth ?? row.dateOfBirth),
    gender: optionalText(row.gender),
    emailPrimary: optionalText(row.email_primary ?? row.emailPrimary),
    emailSecondary: optionalText(row.email_secondary ?? row.emailSecondary),
    mobilePrimary: optionalText(row.mobile_primary ?? row.mobilePrimary),
    mobileSecondary: optionalText(row.mobile_secondary ?? row.mobileSecondary),
    whatsappNumber: optionalText(row.whatsapp_number ?? row.whatsappNumber),
    residentialAddress: address(row.residential_address ?? row.residentialAddress),
    permanentAddress: address(row.permanent_address ?? row.permanentAddress),
    correspondenceAddress: address(row.correspondence_address ?? row.correspondenceAddress),
    status: text(row.status),
  }
}

function filing(row: Raw): ClientFiling {
  return {
    id: text(row.id),
    assessmentYear: text(row.assessment_year ?? row.assessmentYear),
    financialYear: text(row.financial_year ?? row.financialYear),
    itrType: text(row.itr_type ?? row.itrType),
    status: text(row.status),
    dueDate: optionalText(row.due_date ?? row.dueDate),
    filedDate: optionalText(row.filed_date ?? row.filedDate),
    acknowledgementNumber: optionalText(row.acknowledge_number ?? row.acknowledgement_number),
    acknowledgementDate: optionalText(row.acknowledgement_date),
    eVerificationStatus: optionalText(row.e_verification_status),
    totalTaxLiability: number(row.total_tax_liability),
    refundAmount: number(row.refund_amount),
    demandAmount: number(row.demand_amount),
  }
}

function document(row: Raw): ClientDocument {
  return {
    id: text(row.id),
    name: text(row.name ?? row.document_name),
    category: text(row.category),
    status: text(row.status),
    fileSize: number(row.file_size),
    isVerified: Boolean(row.is_verified),
    createdAt: optionalText(row.created_at),
  }
}

function documentRequest(row: Raw): ClientDocumentRequest {
  if (row.customer_type !== 'individual' && row.customer_type !== 'business') {
    throw new Error('Document requests need verified customer links before upload.')
  }
  const financialYear = text(row.financial_year)
  requestAssessmentYear(financialYear)
  return {
    id: text(row.id),
    assessmentId: text(row.assessment_id),
    customerId: text(row.customer_id),
    customerType: row.customer_type,
    financialYear,
    category: optionalText(row.category),
    title: text(row.title),
    description: optionalText(row.description),
    isRequired: Boolean(row.is_required),
    status: text(row.status),
    requestedAt: optionalText(row.requested_at),
    resolutionNotes: optionalText(row.resolution_notes),
  }
}

function requestAssessmentYear(financialYear: string): string {
  if (!/^20\d{2}-\d{2}$/.test(financialYear)) {
    throw new Error('Document request needs a verified financial year before upload.')
  }
  const start = Number(financialYear.slice(0, 4))
  if (start >= 2099 || financialYear.slice(-2) !== String((start + 1) % 100).padStart(2, '0')) {
    throw new Error('Document request needs a verified financial year before upload.')
  }
  return `${start + 1}-${String((start + 2) % 100).padStart(2, '0')}`
}

function reminder(row: Raw): ClientReminder {
  return {
    id: text(row.id),
    title: text(row.title),
    description: optionalText(row.description),
    dueDate: optionalText(row.due_date),
    priority: text(row.priority),
    status: text(row.status),
  }
}

function message(row: Raw): ClientMessage {
  return {
    id: text(row.notification_id ?? row.id),
    subject: optionalText(row.subject),
    message: text(row.message),
    channel: text(row.channel),
    status: text(row.status),
    createdAt: optionalText(row.created_at),
    sentAt: optionalText(row.sent_at),
  }
}

function payment(row: Raw): ClientPayment {
  return {
    id: text(row.payment_id ?? row.id),
    amount: number(row.amount),
    currency: text(row.currency || 'INR'),
    description: optionalText(row.description),
    status: text(row.status),
    transactionId: optionalText(row.transaction_id),
    paidAt: optionalText(row.paid_at),
    createdAt: optionalText(row.created_at),
  }
}

function consent(value: unknown): ClientConsent {
  const row = object(value)
  return {
    id: text(row.id),
    assessmentId: text(row.assessment_id),
    version: number(row.version),
    status: text(row.status),
    statementText: text(row.statement_text),
    requestedAt: optionalText(row.requested_at),
    expiresAt: optionalText(row.expires_at),
    decidedAt: optionalText(row.decided_at),
    decisionNotes: optionalText(row.decision_notes),
  }
}

function profileBody(patch: ClientProfileUpdate): Raw {
  const body: Raw = {}
  if (patch.emailPrimary !== undefined) body.email_primary = patch.emailPrimary
  if (patch.emailSecondary !== undefined) body.email_secondary = patch.emailSecondary
  if (patch.mobilePrimary !== undefined) body.mobile_primary = patch.mobilePrimary
  if (patch.mobileSecondary !== undefined) body.mobile_secondary = patch.mobileSecondary
  if (patch.whatsappNumber !== undefined) body.whatsapp_number = patch.whatsappNumber
  if (patch.correspondenceAddress !== undefined) {
    body.correspondence_address = {
      line1: patch.correspondenceAddress.line1,
      line2: patch.correspondenceAddress.line2,
      city: patch.correspondenceAddress.city,
      state: patch.correspondenceAddress.state,
      pincode: patch.correspondenceAddress.pincode,
    }
  }
  return body
}

async function optional<T>(label: string, operation: () => Promise<T>, fallback: T, warnings: string[]): Promise<T> {
  try {
    return await operation()
  } catch {
    warnings.push(`${label} is temporarily unavailable`)
    return fallback
  }
}

export function createHttpClientPortalService(): ClientPortalService {
  return {
    async load(): Promise<ClientPortalSnapshot> {
      try {
        const profileResponse = await apiClient.get('/customers/me')
        const ownProfile = profile(profileResponse.data)
        const customerId = ownProfile.customerId
        const warnings: string[] = []
        const [filings, documents, documentRequests, reminders, messages, payments, consents] = await Promise.all([
          optional('Filing history', async () => items((await apiClient.get(`/tax-assessments/customer/${customerId}`)).data).map(filing), [], warnings),
          optional('Documents', async () => items((await apiClient.get(`/documents/by-entity/individual_customer/${customerId}`, { params: { page: 1, page_size: 100 } })).data).map(document), [], warnings),
          optional('Document requests', async () => items((await apiClient.get('/document-requests', { params: { customer_id: customerId, customer_type: 'individual' } })).data).map(documentRequest), [], warnings),
          optional('Reminders', async () => items((await apiClient.get(`/reminders/by-entity/individual_customer/${customerId}`, { params: { page: 1, page_size: 100 } })).data).map(reminder), [], warnings),
          optional('Messages', async () => items((await apiClient.get('/notifications', { params: { page: 1, page_size: 50 } })).data).map(message), [], warnings),
          optional('Payment history', async () => items((await apiClient.get(`/payments/customer/${customerId}`, { params: { page: 1, page_size: 100 } })).data).map(payment), [], warnings),
          optional('Consent history', async () => items((await apiClient.get('/tax-assessments/consents')).data).map(consent), [], warnings),
        ])
        return { profile: ownProfile, filings, documents, documentRequests, reminders, messages, payments, consents, warnings }
      } catch (error) {
        throw toServiceError(error)
      }
    },

    async updateProfile(patch: ClientProfileUpdate): Promise<ClientProfile> {
      try {
        return profile((await apiClient.patch('/customers/me', profileBody(patch))).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },

    async uploadForRequest(request: ClientDocumentRequest, file: File): Promise<void> {
      try {
        if (request.customerType !== 'individual' && request.customerType !== 'business') {
          throw new Error('Document request needs a verified customer link before upload.')
        }
        const assessmentYear = requestAssessmentYear(request.financialYear)
        const form = new FormData()
        form.append('file', file)
        const uploaded = await apiClient.post('/documents/upload', form, {
          params: {
            category: request.category || 'other',
            linked_entity_type: `${request.customerType}_customer`,
            linked_entity_id: request.customerId,
            assessment_year: assessmentYear,
            workspace_service: 'income_tax',
            description: `Response to: ${request.title}`,
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        await apiClient.post(`/document-requests/${request.id}/supply`, {
          document_id: text(object(uploaded.data).id),
        })
      } catch (error) {
        throw toServiceError(error)
      }
    },

    async decideConsent(consentId, decision, notes): Promise<ClientConsent> {
      try {
        return consent((await apiClient.post(`/tax-assessments/consents/${consentId}/decision`, {
          decision,
          notes: notes || null,
        })).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },

    async downloadDocument(documentId: string): Promise<Blob> {
      try {
        const response = await apiClient.get(`/documents/${documentId}/content`, { responseType: 'blob' })
        return response.data as Blob
      } catch (error) {
        throw toServiceError(error)
      }
    },
  }
}
